// store/slices/orderListSlice.ts

import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { OrderInterface } from "../../common/types/types";
import { RootState } from "../store";
import { selectAllSaleItems } from "./saleItemListSlice";
import { OrderStatus } from "../../common/types/enums";

interface OrdersState {
  data: OrderInterface[];
}

const initialState: OrdersState = {
  data: [],
};

export const orderListSlice = createSlice({
  name: "orderList",
  initialState,
  reducers: {
    setOrders: (state, action: PayloadAction<OrderInterface[]>) => {
      state.data = action.payload;
    },

    addOrder: (state, action: PayloadAction<OrderInterface>) => {
      state.data.unshift(action.payload);
    },

    updateOrderStatus: (
      state,
      action: PayloadAction<{
        orderId: number;
        status: OrderStatus;
      }>,
    ) => {
      const order = state.data.find((o) => o.id === action.payload.orderId);
      if (order) {
        order.status = action.payload.status;
        order.updatedAt = new Date().toISOString();
      }
    },

    clearOrders: (state) => {
      state.data = [];
    },

    removeOrder: (state, action: PayloadAction<number>) => {
      state.data = state.data.filter((order) => order.id !== action.payload);
    },
  },
});

export const {
  setOrders,
  addOrder,
  updateOrderStatus,
  clearOrders,
  removeOrder,
} = orderListSlice.actions;

export const selectOrders = (state: RootState) => state.orderList.data;

export const selectOrderById = (orderId: number) => (state: RootState) =>
  state.orderList.data.find((order) => order.id === orderId);

// Все неотменённые заказы
export const selectNonCancelledOrders = (state: RootState) =>
  state.orderList.data.filter(
    (order) => order.status !== OrderStatus.CANCELLED,
  );

// Есть ли хоть один неотменённый заказ (глобально)
export const selectHasNonCancelledOrders = createSelector(
  [selectNonCancelledOrders],
  (orders) => orders.length > 0,
);

// Set купленных itemId
export const selectPurchasedItemIds = createSelector(
  [selectNonCancelledOrders],
  (orders) => {
    const purchasedSet = new Set<number>();
    orders.forEach((order) => {
      order.items?.forEach((item) => {
        purchasedSet.add(Number(item.itemId));
      });
    });
    return purchasedSet;
  },
);

// Set categoryId, которые были куплены (через связку с товарами)
export const selectPurchasedCategoryIds = createSelector(
  [selectNonCancelledOrders, selectAllSaleItems],
  (orders, allItems) => {
    const purchasedCategorySet = new Set<number>();

    // Маппинг itemId -> categoryId
    const itemToCategoryMap = new Map<number, number>();
    allItems.forEach((item) => {
      itemToCategoryMap.set(Number(item.id), Number(item.categoryId));
    });

    orders.forEach((order) => {
      order.items?.forEach((item) => {
        const categoryId = itemToCategoryMap.get(Number(item.itemId));
        if (categoryId) {
          purchasedCategorySet.add(categoryId);
        }
      });
    });

    return purchasedCategorySet;
  },
);

// Set storeId, где были заказы
export const selectPurchasedStoreIds = createSelector(
  [selectNonCancelledOrders],
  (orders) => {
    const storeSet = new Set<number>();
    orders.forEach((order) => {
      if (order.storeId) {
        storeSet.add(order.storeId);
      }
    });
    return storeSet;
  },
);

// Покупка конкретного товара (по itemId) - для единичных проверок
export const selectHasPurchasedItem = (itemId: number) =>
  createSelector([selectPurchasedItemIds], (purchasedSet) =>
    purchasedSet.has(itemId),
  );

// Покупка в магазине/точке - для единичных проверок
export const selectHasOrderInStore = (storeId: number) =>
  createSelector([selectPurchasedStoreIds], (storeSet) =>
    storeSet.has(storeId),
  );

export const selectPurchasedItemsMap = createSelector(
  [selectNonCancelledOrders],
  (orders) => {
    const purchasedMap = new Map<number, boolean>();
    orders.forEach((order) => {
      order.items?.forEach((item) => {
        purchasedMap.set(Number(item.itemId), true);
      });
    });
    return purchasedMap;
  },
);

export default orderListSlice.reducer;
